// Looking back (roadmap Batch 5) — the pure core behind Year in review (#24),
// stats over time (#25), true "times finished" (#4) and the diary CSV (#27).
// Everything is computed from History events (design_documents/history.md).

const live = (events) => (events ?? []).filter((e) => !e.deleted);
const isFinish = (e) => e.kind === "engagement" && e.to_value === "done";
const isStart = (e) => e.kind === "engagement" && (e.to_value === "playing" || e.to_value === "replaying");
const yearOf = (iso) => Number(String(iso ?? "").slice(0, 4)) || null;
const monthOf = (iso) => String(iso ?? "").slice(0, 7);

// item_id → how many times History saw it set to Done (re-finishes included).
export function finishCounts(events) {
	const out = new Map();
	for (const e of live(events)) if (isFinish(e) && e.item_id) out.set(e.item_id, (out.get(e.item_id) ?? 0) + 1);
	return out;
}

// Years that have any activity, newest first.
export function reviewYears(events) {
	return [...new Set(live(events).map((e) => yearOf(e.at)).filter(Boolean))].sort((a, b) => b - a);
}

// One year, across every category.
//   events    — History events
//   mediaById — Map item id → normalized item (db/media.js), for ratings/covers
export function yearReview(events, mediaById, year) {
	const inYear = live(events).filter((e) => yearOf(e.at) === year);
	const finishedMap = new Map();
	for (const e of inYear.filter(isFinish).sort((a, b) => a.at.localeCompare(b.at))) {
		const prev = finishedMap.get(e.item_id);
		if (prev) prev.times += 1;
		else {
			finishedMap.set(e.item_id, {
				item_id: e.item_id,
				at: e.at,
				times: 1,
				title: mediaById.get(e.item_id)?.title ?? e.title ?? "",
				media_type: e.media_type ?? mediaById.get(e.item_id)?.media_type ?? null,
				item: mediaById.get(e.item_id) ?? null,
				cover_url: mediaById.get(e.item_id)?.cover_url ?? e.cover_url ?? null,
				route: mediaById.get(e.item_id)?.route ?? e.route ?? null,
			});
		}
	}
	const finished = [...finishedMap.values()];
	const byMedia = {};
	for (const f of finished) byMedia[f.media_type] = (byMedia[f.media_type] ?? 0) + 1;

	const rated = finished.filter((f) => f.item?.rating);
	const topRated = [...rated].sort((a, b) => b.item.rating - a.item.rating || a.at.localeCompare(b.at)).slice(0, 5);
	const avgRating = rated.length ? Math.round((rated.reduce((s, f) => s + f.item.rating, 0) / rated.length) * 10) / 10 : null;

	const perMonth = new Map();
	for (const f of finished) perMonth.set(monthOf(f.at), (perMonth.get(monthOf(f.at)) ?? 0) + 1);
	let busiest = null;
	for (const [month, count] of perMonth) if (!busiest || count > busiest.count) busiest = { month, count };

	const awards = {};
	for (const e of inYear) if (e.kind === "award" && e.to_value) awards[e.to_value] = (awards[e.to_value] ?? 0) + 1;

	return {
		year,
		finished,
		finishedCount: finished.length,
		byMedia,
		added: inYear.filter((e) => e.kind === "added").length,
		started: new Set(inYear.filter(isStart).map((e) => e.item_id)).size,
		episodes: inYear.filter((e) => e.kind === "seen").reduce((n, e) => n + (e.detail?.episodes?.length ?? 0), 0),
		topRated,
		avgRating,
		busiestMonth: busiest,
		awards,
		first: finished[0] ?? null,
		last: finished[finished.length - 1] ?? null,
	};
}

// The last `months` months, oldest first: finished, added, average rating of
// what was finished (when rated).
export function monthlyStats(events, mediaById, { today, months = 12 } = {}) {
	const [y, m] = String(today).split("-").map(Number);
	const keys = [];
	for (let i = months - 1; i >= 0; i--) {
		const d = new Date(Date.UTC(y, m - 1 - i, 1));
		keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`);
	}
	const rows = new Map(keys.map((k) => [k, { month: k, finished: 0, added: 0, ratings: [] }]));
	for (const e of live(events)) {
		const row = rows.get(monthOf(e.at));
		if (!row) continue;
		if (e.kind === "added") row.added += 1;
		if (isFinish(e)) {
			row.finished += 1;
			const r = mediaById.get(e.item_id)?.rating;
			if (r) row.ratings.push(r);
		}
	}
	return [...rows.values()].map(({ ratings, ...r }) => ({
		...r,
		avgRating: ratings.length ? Math.round((ratings.reduce((s, x) => s + x, 0) / ratings.length) * 10) / 10 : null,
	}));
}

// ---------- diary CSV ----------

function csvCell(v) {
	const s = v == null ? "" : String(v);
	return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// History as a diary CSV, oldest first. `labelOf(event)` gives the readable
// "what happened" (utils/history.js eventLabel).
export function historyCsv(events, labelOf = (e) => e.kind) {
	const header = ["date", "time", "category", "title", "what", "kind", "from", "to", "reconstructed"];
	const rows = live(events)
		.slice()
		.sort((a, b) => a.at.localeCompare(b.at))
		.map((e) => [
			e.at.slice(0, 10),
			e.at.slice(11, 16),
			e.media_type ?? "",
			e.title ?? "",
			labelOf(e),
			e.kind,
			e.from_value ?? "",
			e.to_value ?? "",
			e.reconstructed ? "yes" : "",
		]);
	return [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
}
